import React, { FunctionComponent, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import Label from 'components/Form/Label';
import ClearSelect from 'components/Form/ClearSelect';
import { IOption } from 'components/Form/Select/SingleSelect';
import StyledErrorMessage from 'components/Form/StyledErrorMessage';
import Wrapper from 'components/Form/Wrapper';
import Row from 'components/Row';
import { isBefore, startOfDay, endOfDay, addDays, isAfter, isEqual, startOfHour } from 'date-fns';
import { FieldProps, getIn } from 'formik';
import Select from 'react-select';
import { ValueType } from 'react-select/lib/types';
import { addTimeToDate, eachHour } from 'libs/dateTimeHelpers';
import styled from 'styled-components';
import './style.css';
import Minutes from './Minutes';

interface ITimePicker {
  className?: string;
  label?: string;
  placeholder?: string;
  step?: number;
  min?: Date | string | null;
  max?: Date | string | null;
  excludedTimes?: Array<{ start: Date; end: Date }>;
  onlyHours?: boolean;
  isClearable?: boolean;
  hourNamePrefix?: string;
  minuteNamePrefix?: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  hourInputProps?: object;
  // eslint-disable-next-line @typescript-eslint/ban-types
  minuteInputProps?: object;
}

const TimePicker: FunctionComponent<ITimePicker & FieldProps> = ({
  className = '',
  label = '',
  // placeholder, // maybe will use in future
  step = 15,
  min,
  max,
  onlyHours = false,
  isClearable,
  hourNamePrefix = '_hours',
  minuteNamePrefix = '_minutes',
  excludedTimes = [],
  hourInputProps,
  minuteInputProps,
  field,
  form,
  ...rest
}) => {
  const { name, value, onBlur } = field;
  const { errors, touched, setFieldValue, values } = form;
  const hoursRef = useRef<Select<IOption>>(null);
  const hoursName = useMemo(() => name + hourNamePrefix, [name, hourNamePrefix]);
  const minutesName = useMemo(() => name + minuteNamePrefix, [name, minuteNamePrefix]);
  const hasError = useMemo(
    () => Boolean(getIn(errors, name) && getIn(touched, name)),
    [errors, touched, name]
  );
  const hasHoursError = useMemo(
    () => Boolean(getIn(errors, hoursName)),
    [errors, touched, hoursName]
  );
  const hasCommonError = useMemo(
    () => hasError && !hasHoursError && !Boolean(getIn(errors, minutesName)),
    [hasError, hasHoursError]
  );

  const isExcludeTime = useCallback(
    (time: Date) =>
      excludedTimes.find((range) => {
        return (
          (isEqual(time, range.start) || isAfter(time, range.start)) && isBefore(time, range.end)
        );
      }),
    [excludedTimes]
  );

  const minDate = useMemo(() => {
    if (min) {
      return min instanceof Date ? min : new Date(`${new Date().toDateString()} ${min}`);
    } else {
      return startOfDay(new Date());
    }
  }, [min]);

  const maxDate = useMemo(() => {
    if (max) {
      return max instanceof Date ? max : new Date(`${new Date().toDateString()} ${max}`);
    } else {
      return endOfDay(new Date());
    }
  }, [max]);

  const availableTimes = useCallback(
    (date: Date) => {
      const hour = date.getHours();
      return Array.from({ length: 60 / step }, (item, index) =>
        addTimeToDate(date, `${hour}:${index * step}`)
      ).filter((time: Date) =>
        isBefore(time, minDate) || isAfter(time, maxDate) || isExcludeTime(time) ? false : true
      );
    },
    [step, isExcludeTime, minDate, maxDate]
  );

  const hourOptions = useMemo(() => {
    const endOfRange = isBefore(minDate, maxDate) ? maxDate : addDays(maxDate, 1);
    return eachHour(startOfHour(minDate), startOfHour(endOfRange)).reduce<any[]>(
      (options, time) => {
        availableTimes(time).length > 0 &&
          options.push({
            label: time.toLocaleString('en-US', {
              hour: 'numeric',
              minute: onlyHours ? 'numeric' : undefined,
              hour12: true,
            }),
            value: time.getHours(),
            date: time,
          });
        return options;
      },
      []
    );
  }, [onlyHours, minDate, maxDate, availableTimes]);

  const minuteOptions: IOption[] = useMemo(() => {
    if (!values[hoursName]) return [];

    return availableTimes(values[hoursName].date).map((time) => {
      const minutes = time.getMinutes();
      return {
        label: minutes < 10 ? '0' + minutes : minutes.toString(),
        value: minutes,
      };
    });
  }, [values[hoursName], availableTimes]);

  const onChangeHours = useCallback(
    (selected: ValueType<IOption>) => setFieldValue(hoursName, selected),
    [hoursName, setFieldValue]
  );

  useEffect(() => {
    if (!Boolean(value)) return;
    const [hours] = value.split(':');
    if (hours && getIn(values, [hoursName, 'value']) !== Number(hours)) {
      const selectedHour = hourOptions.find((o) => o.value === Number(hours));
      selectedHour && onChangeHours(selectedHour);
    }
  }, [value]);

  // clear hours field if current not exist in list of hours
  useEffect(() => {
    const hour = getIn(values, [hoursName, 'value']);
    if (hour && !hourOptions.find((option) => option.value === hour)) {
      if (hoursRef.current !== null) {
        hoursRef.current.onChange(undefined, { action: 'clear' });
      }
      onChangeHours(undefined);
      setFieldValue(name, '');
    }
  }, [hourOptions, values[hoursName], onChangeHours, name, setFieldValue, hoursRef]);

  useEffect(() => {
    const hours = values[hoursName];
    if (hours) {
      if (onlyHours) {
        setFieldValue(name, `${(hours as IOption).value}:00`, true);
      } else {
        const minutes = values[minutesName];
        if (hours && minutes) {
          setFieldValue(name, `${(hours as IOption).value}:${(minutes as IOption).value}`, true);
        }
      }
    }
  }, [values[minutesName], values[hoursName], name, setFieldValue, onlyHours]);

  const onClear = useCallback(() => {
    if (isClearable) {
      onChangeHours(undefined);
      setFieldValue(name, '');
    }
  }, [name, isClearable, setFieldValue, onChangeHours]);

  return (
    <Wrapper className={className}>
      {label && <Label>{label}</Label>}
      <RelativeRow alignItems="center" className={className}>
        <div>
          <ClearSelect
            ref={hoursRef}
            name={hoursName}
            options={hourOptions}
            error={hasError || hasHoursError}
            onChange={onChangeHours}
            onBlur={onBlur}
            value={values[hoursName]}
            placeholder="Hours"
            isClearable={isClearable}
            onClear={onClear}
            {...hourInputProps}
          />
          {hasHoursError && <StyledErrorMessage>{errors[hoursName]}</StyledErrorMessage>}
        </div>
        {!onlyHours && (
          <Minutes
            options={minuteOptions}
            minuteInputProps={minuteInputProps}
            onlyHours={onlyHours}
            hoursName={hoursName}
            minutesName={minutesName}
            step={step}
            onClear={onClear}
            form={form}
            field={field}
            {...rest}
          />
        )}
      </RelativeRow>
      {hasCommonError && <StyledErrorMessage>{errors[name]}</StyledErrorMessage>}
    </Wrapper>
  );
};

export default memo(TimePicker);

const RelativeRow = styled(Row)`
  position: relative;
  align-items: flex-start;
`;
